import React, { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { WHATSAPP, IMAGES } from "../mock";
import SectionHeader from "./SectionHeader";
import useReveal from "../hooks/useReveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const initial = {
  name: "",
  phone: "",
  email: "",
  serviceType: "",
  tripType: "oneway",
  passengers: "2",
  origin: "",
  destination: "",
  date: "",
  time: "",
  flight: "",
  notes: "",
};

export default function BookingForm() {
  const { t, S, lang } = useLang();
  const ref = useReveal();
  const [form, setForm] = useState(initial);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const F = S.form;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.serviceType || !form.origin || !form.destination || !form.date) {
      toast.error(lang === "es" ? "Completa los campos obligatorios" : "Please complete the required fields");
      return;
    }
    const lines = [
      `*XPERIENCIAS MEXICO - ${lang === "es" ? "Solicitud de traslado" : "Transfer request"}*`,
      `${t(F.name)}: ${form.name}`,
      `${t(F.phone)}: ${form.phone}`,
      form.email && `${t(F.email)}: ${form.email}`,
      `${t(F.serviceType)}: ${form.serviceType}`,
      `${t(F.tripType)}: ${form.tripType === "oneway" ? t(F.oneway) : t(F.roundtrip)}`,
      `${t(F.passengers)}: ${form.passengers}`,
      `${t(F.origin)}: ${form.origin}`,
      `${t(F.destination)}: ${form.destination}`,
      `${t(F.date)}: ${form.date} ${form.time}`,
      form.flight && `${t(F.flight)}: ${form.flight}`,
      form.notes && `${t(F.notes)}: ${form.notes}`,
    ].filter(Boolean);
    const url = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
    toast.success(lang === "es" ? "Abriendo WhatsApp..." : "Opening WhatsApp...");
  };

  const serviceOptions = [F.st_airport_hotel, F.st_hotel_airport, F.st_park, F.st_tour];

  return (
    <section id="reservar" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          <div className="reveal rounded-3xl overflow-hidden relative min-h-[320px] hidden lg:block">
            <img src={IMAGES.arte} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#042f37]/90 to-[#042f37]/20" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-display font-extrabold text-2xl text-white">{t(S.contact.title)}</h3>
              <p className="mt-2 text-white/80">{t(F.sub)}</p>
            </div>
          </div>

          <div>
            <SectionHeader kicker={t(F.kicker)} title={t(F.title)} sub={t(F.sub)} center={false} />

            <form onSubmit={submit} className="mt-8 reveal grid sm:grid-cols-2 gap-5">
              <Field label={t(F.name)} req>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t(F.name)} />
              </Field>
              <Field label={t(F.phone)} req>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52 ..." />
              </Field>
              <Field label={t(F.email)}>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
              </Field>
              <Field label={t(F.serviceType)} req>
                <Select value={form.serviceType} onValueChange={(v) => set("serviceType", v)}>
                  <SelectTrigger><SelectValue placeholder={t(F.serviceType)} /></SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((o) => (
                      <SelectItem key={o.en} value={t(o)}>{t(o)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t(F.tripType)}>
                <Select value={form.tripType} onValueChange={(v) => set("tripType", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oneway">{t(F.oneway)}</SelectItem>
                    <SelectItem value="roundtrip">{t(F.roundtrip)}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t(F.passengers)} req>
                <Input type="number" min="1" value={form.passengers} onChange={(e) => set("passengers", e.target.value)} />
              </Field>
              <Field label={t(F.origin)} req>
                <Input value={form.origin} onChange={(e) => set("origin", e.target.value)} placeholder="Cancún Airport" />
              </Field>
              <Field label={t(F.destination)} req>
                <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Hotel Xcaret" />
              </Field>
              <Field label={t(F.date)} req>
                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
              </Field>
              <Field label={t(F.time)}>
                <Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} />
              </Field>
              <Field label={t(F.flight)}>
                <Input value={form.flight} onChange={(e) => set("flight", e.target.value)} placeholder="AA 1234" />
              </Field>
              <div className="sm:col-span-2">
                <Label className="mb-2 block text-sm font-medium">{t(F.notes)}</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full h-13">
                  <Send className="w-5 h-5" />
                  {t(F.submit)}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, req, children }) {
  return (
    <div>
      <Label className="mb-2 block text-sm font-medium">
        {label} {req && <span className="text-accent">*</span>}
      </Label>
      {children}
    </div>
  );
}
